import styled, { createGlobalStyle } from 'styled-components';
import { Card, Button, Input, Select, Table, Row, Space } from 'antd';
import { COLORS } from '../../constants/colors';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 90px);
  overflow: hidden;

  /* On mobile the page flows naturally and the admin content area scrolls,
     so the intake cards aren't trapped in a fixed-height, clipped box. */
  @media (max-width: 768px) {
    height: auto;
    overflow: visible;
  }

  /* Animation Keyframes */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Header Animations - Faster and more subtle */
  .header-animate-in {
    animation: fadeInDown 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .header-animate-out {
    opacity: 1; /* Keep visible */
  }
  
  /* Statistics Animations - Faster and more subtle */
  .stats-animate-in {
    animation: slideInLeft 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .stats-animate-out {
    opacity: 1; /* Keep visible */
  }
  
  /* Filters Animations - Faster and more subtle */
  .filters-animate-in {
    animation: slideInLeft 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .filters-animate-out {
    opacity: 1; /* Keep visible */
  }
  
  /* Table Animations - Faster and more subtle */
  .table-animate-in {
    animation: fadeInUp 0.3s ease-out forwards;
    opacity: 1; /* Start visible for better UX */
  }
  
  .table-animate-out {
    opacity: 1; /* Keep visible */
  }
`;

export const QueueCard = styled(Card)`
  background: ${COLORS.gradient.subtleGray};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${COLORS.cardShadow};
  transition: all 0.3s ease;
  
  /* Animation support */
  animation-fill-mode: both;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${COLORS.cardShadowDark};
    border-color: ${COLORS.$primaryBlue};
  }
  
  .ant-card-head {
    background: ${COLORS.gradient.blackSecondary};
    border-bottom: 2px solid ${COLORS.$primaryBlue};
    border-radius: 16px 16px 0 0;
    padding: 16px 24px;
    
    .ant-card-head-title {
      color: ${COLORS.textWhite};
      font-weight: 600;
      font-size: 16px;
    }
  }
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const StatusCard = styled(Card)`
  background: ${COLORS.gradient.subtleGray};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${COLORS.cardShadow};
  margin-bottom: 16px;
  transition: all 0.3s ease;
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  /* Animation support */
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${COLORS.cardShadowDark};
    border-color: ${COLORS.$primaryBlue};
  }
  
  .ant-card-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }
  
  .ant-statistic {
    text-align: center;
    
    .ant-statistic-title {
      color: ${COLORS.textMedium};
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 8px;
    }
    
    .ant-statistic-content {
      color: ${COLORS.textDark};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      .ant-statistic-content-value {
        font-size: 32px;
        font-weight: 700;
        line-height: 1.2;
      }
      
      .ant-statistic-content-prefix {
        font-size: 20px;
        margin-right: 4px;
      }
    }
  }
`;

export const ActionButton = styled(Button)`
  min-width: 110px !important;
  height: 36px !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: 500;
  font-size: 12px !important;
  border: 1px solid transparent !important;
  transition: all 0.2s ease !important;
  white-space: nowrap !important;
  margin: 0 2px !important;
  
  /* Ensure button text is always visible and properly styled */
  span {
    font-weight: 500;
    font-size: inherit;
    white-space: nowrap;
  }
  
  &.ant-btn-primary {
    background: linear-gradient(135deg, ${COLORS.$primaryBlue}, ${COLORS.$primaryBlueDark}) !important;
    border: none !important;
    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2) !important;
    
    &:hover {
      background: linear-gradient(135deg, ${COLORS.$primaryBlueDark}, ${COLORS.$primaryBlue}) !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 8px rgba(24, 144, 255, 0.3) !important;
    }
    
    &:focus {
      background: linear-gradient(135deg, ${COLORS.$primaryBlue}, ${COLORS.$primaryBlueDark}) !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    }
    
    &:active {
      transform: translateY(0) !important;
      box-shadow: 0 1px 2px rgba(24, 144, 255, 0.4) !important;
    }
  }
  
  &:not(.ant-btn-primary) {
    background: #fafafa !important;
    border: 1px solid #e8e8e8 !important;
    color: ${COLORS.textDark} !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
    
    &:hover {
      background: ${COLORS.$primaryBlue} !important;
      color: white !important;
      border-color: ${COLORS.$primaryBlue} !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 8px rgba(24, 144, 255, 0.25) !important;
    }
    
    &:focus {
      border-color: ${COLORS.$primaryBlue} !important;
      color: ${COLORS.$primaryBlue} !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.15) !important;
    }
    
    &:active {
      transform: translateY(0) !important;
      box-shadow: 0 1px 2px rgba(24, 144, 255, 0.3) !important;
    }
  }
`;

export const StyledInput = styled(Input)`
  height: 48px !important;
  border-radius: 10px !important;
  border: 1px solid ${COLORS.border} !important;
  box-shadow: none !important;
  font-size: 16px !important;
  font-weight: 400 !important;
  transition: all 0.2s ease !important;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  }
  
  &:focus,
  &.ant-input-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-input-prefix {
    margin-right: 12px;
    font-size: 16px;
  }
  
  &::placeholder {
    color: ${COLORS.textMuted};
    font-weight: 400;
  }
`;

export const StyledSelect = styled(Select)`
  height: 48px !important;
  
  .ant-select-selector {
    height: 48px !important;
    border-radius: 10px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: none !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    padding: 0 16px !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      border-color: ${COLORS.$primaryBlue} !important;
      box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
    }
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-select-selection-item {
    line-height: 44px !important;
    height: 44px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    font-weight: 400 !important;
  }
  
  .ant-select-selection-placeholder {
    line-height: 44px !important;
    height: 44px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    color: ${COLORS.textMuted} !important;
    font-weight: 400 !important;
  }
  
  .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 16px !important;
    font-size: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 16px !important;
    margin-top: 0 !important;
    
    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      vertical-align: middle !important;
      line-height: 1 !important;
    }
  }
`;

export const StyledTable = styled(Table)`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  /* Full-height scrollable table layout */
  &.ant-table-wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-spin-nested-loading {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .ant-spin-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table-container {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table-body {
    flex: 1 !important;
    max-height: unset !important;
    overflow-y: auto !important;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }
  
  /* Clean row highlighting for different states */
  .in-progress-row {
    background: linear-gradient(90deg, rgba(24, 144, 255, 0.03), rgba(24, 144, 255, 0.01)) !important;
    border-left: 3px solid ${COLORS.$primaryBlue};
    
    &:hover > td {
      background: linear-gradient(90deg, rgba(24, 144, 255, 0.06), rgba(24, 144, 255, 0.02)) !important;
    }
  }
  
  .urgent-row {
    background: linear-gradient(90deg, rgba(255, 152, 0, 0.03), rgba(255, 152, 0, 0.01)) !important;
    border-left: 3px solid ${COLORS.warningAmber};
    
    &:hover > td {
      background: linear-gradient(90deg, rgba(255, 152, 0, 0.06), rgba(255, 152, 0, 0.02)) !important;
    }
  }
    /* Pagination styling */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        height: 32px !important;
        
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          box-sizing: border-box !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          flex: 1 !important;
          min-width: 0 !important;
        }
        
        .ant-select-selection-search {
          height: 28px !important;
          padding-left: 8px !important;
          
          .ant-select-selection-search-input {
            height: 28px !important;
            line-height: 28px !important;
          }
        }
        
        .ant-select-arrow {
          right: 10px !important;
          flex-shrink: 0 !important;
          height: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          
          .anticon {
            line-height: 1 !important;
            vertical-align: middle !important;
          }
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
        
        /* Dropdown menu styling */
        .ant-select-dropdown {
          border-radius: 10px !important;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15) !important;
          border: 1px solid ${COLORS.border} !important;
          
          .ant-select-item {
            padding: 8px 12px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            min-height: 32px !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
            display: flex !important;
            align-items: center !important;
            
            &:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              color: ${COLORS.$primaryBlue} !important;
            }
            
            &.ant-select-item-option-selected {
              background: ${COLORS.$primaryBlue} !important;
              color: white !important;
              font-weight: 600 !important;
              
              .ant-select-item-option-state {
                color: white !important;
              }
              
              &:hover {
                background: ${COLORS.$primaryBlueDark} !important;
                color: white !important;
              }
            }
            
            .ant-select-item-option-state {
              color: ${COLORS.$primaryBlue} !important;
              
              .anticon {
                font-weight: bold !important;
              }
            }
          }
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s ease !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      /* Always show the arrow icons instead of the misaligned dots */
      .ant-pagination-item-container {
        .ant-pagination-item-ellipsis {
          display: none !important;
        }
        .ant-pagination-item-link-icon {
          display: inline-block !important;
          opacity: 1 !important;
          color: ${COLORS.textMuted};
          font-size: 14px;
        }
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .ant-pagination-item-container .ant-pagination-item-link-icon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Custom pagination class for better control */
  &.patient-queue-pagination {
    .ant-pagination {
      justify-content: space-between;
      
      @media (max-width: 768px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between; /* Left/right alignment */
        gap: 8px;
        flex-wrap: nowrap; /* Single row */
        
        /* Hide total text for compact mobile layout */
        .ant-pagination-total-text {
          display: none !important;
        }
        
        .ant-pagination-options {
          margin-left: auto; /* Push to right side */
          order: 2; /* Ensure it comes after pagination items */
          
          .ant-pagination-options-size-changer {
            .ant-select-selector {
              height: 33px !important; /* Updated height as requested */
              min-height: 33px !important;
              font-size: 12px !important;
              padding: 0 8px !important;
            }
          }
        }
        
        /* Create a compact container for pagination buttons - LEFT SIDE */
        .ant-pagination-item,
        .ant-pagination-prev,
        .ant-pagination-next,
        .ant-pagination-jump-prev,
        .ant-pagination-jump-next {
          flex: 0 0 auto !important;
          width: auto !important;
          min-width: 32px !important;
          max-width: 36px !important;
          height: 33px !important; /* Updated height as requested */
          margin: 0 1px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 13px !important;
          line-height: 31px !important; /* Adjusted for new height */
          order: 1; /* Ensure they come before page size selector */
        }
      }
    }
  }
    }
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
    
    /* Adjust column widths for medium screens */
    .ant-table-thead > tr > th:nth-child(1),
    .ant-table-tbody > tr > td:nth-child(1) {
      width: 70px !important;
      min-width: 70px !important;
    }
    
    .ant-table-thead > tr > th:nth-child(2),
    .ant-table-tbody > tr > td:nth-child(2) {
      width: 180px !important;
      min-width: 180px !important;
    }
    
    .ant-table-thead > tr > th:last-child,
    .ant-table-tbody > tr > td:last-child {
      width: auto !important;
      min-width: 400px !important;
    }
  }

  @media (max-width: 768px) {
    /* Allow horizontal scrolling on mobile */
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    
    /* Allow flexible column widths on mobile */
    .ant-table-thead > tr > th:nth-child(1),
    .ant-table-tbody > tr > td:nth-child(1) {
      width: 50px !important;
      min-width: 50px !important;
    }
    
    .ant-table-thead > tr > th:nth-child(2),
    .ant-table-tbody > tr > td:nth-child(2) {
      width: 140px !important;
      min-width: 140px !important;
      white-space: normal !important;
      word-wrap: break-word !important;
    }
    
    .ant-table-thead > tr > th:last-child,
    .ant-table-tbody > tr > td:last-child {
      width: 50px !important;
      min-width: 50px !important;
      max-width: 50px !important;
      white-space: nowrap !important;
      padding: 8px 4px !important;
    }
    
    /* Patient info column - allow text wrapping */
    .ant-table-tbody > tr > td:nth-child(2) {
      .ant-space-vertical {
        min-height: auto;
        
        .ant-typography {
          font-size: 12px !important;
          line-height: 1.3 !important;
          margin-bottom: 2px !important;
          word-break: break-word !important;
          white-space: normal !important;
        }
        
        .ant-typography:first-child {
          font-weight: 600 !important;
          font-size: 13px !important;
        }
      }
    }
    
    /* Action buttons - make icon-only on mobile */
    .ant-space .ant-space-item {
      .ant-btn {
        min-width: 36px !important;
        max-width: 36px !important;
        width: 36px !important;
        height: 36px !important;
        font-size: 14px !important;
        padding: 0 !important;
        
        span:not(.anticon) {
          display: none !important;
        }
      }
    }
    
    /* Tags - make them smaller */
    .ant-tag {
      font-size: 10px !important;
      padding: 2px 6px !important;
    }
  }

  @media (max-width: 576px) {
    /* Extra small mobile devices */
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
      font-size: 11px;
    }
    
    .ant-table-thead > tr > th {
      height: 44px !important;
      font-size: 10px !important;
    }
    
    .ant-table-thead > tr > th:nth-child(1),
    .ant-table-tbody > tr > td:nth-child(1) {
      width: 40px !important;
      min-width: 40px !important;
    }
    
    .ant-table-thead > tr > th:nth-child(2),
    .ant-table-tbody > tr > td:nth-child(2) {
      width: 120px !important;
      min-width: 120px !important;
    }
    
    .ant-table-thead > tr > th:last-child,
    .ant-table-tbody > tr > td:last-child {
      width: 44px !important;
      min-width: 44px !important;
      max-width: 44px !important;
      padding: 6px 2px !important;
    }
    
    /* Even smaller action buttons - icon only */
    .ant-space .ant-space-item {
      .ant-btn {
        min-width: 32px !important;
        max-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        font-size: 12px !important;
        padding: 0 !important;
        
        span:not(.anticon) {
          display: none !important;
        }
      }
    }
    
    /* Very small tags */
    .ant-tag {
      font-size: 9px !important;
      padding: 1px 4px !important;
    }
    
    /* Badge adjustments */
    .ant-badge .ant-badge-count {
      font-size: 8px !important;
      min-width: 16px !important;
      height: 16px !important;
      line-height: 14px !important;
    }
  }
`;

export const FilterContainer = styled.div`
  margin: 0;
`;

export const TableContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLORS.border};
`;

export const HeaderContainer = styled(Row)`
  justify-content: space-between;
  align-items: middle;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid ${COLORS.border};
  
  /* Full-width border extending to edges */
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
  
  @media (max-width: 768px) {
    margin-left: -20px;
    margin-right: -20px;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

export const QueueTabsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 16px 0 8px;
  gap: 4px;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const QueueTabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  color: ${props => props.$active ? COLORS.$primaryBlue : COLORS.textMedium};
  font-weight: ${props => props.$active ? 600 : 500};
  font-size: 14px;
  background: ${props => props.$active ? '#eef4ff' : 'transparent'};
  border: ${props => props.$active ? '1px solid rgba(24, 144, 255, 0.3)' : '1px solid transparent'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,.06)' : 'none'};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  min-height: 40px;
  transition: background-color .2s ease, color .2s ease;
  font-family: inherit;
  line-height: 1;
  
  &:hover {
    color: ${COLORS.textDark};
    background: ${props => props.$active ? '#eef4ff' : 'rgba(24, 144, 255, 0.08)'};
  }
  
  svg {
    font-size: 14px;
    width: 14px;
    height: 14px;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    min-height: 36px;
    gap: 6px;
  }
  
  @media (max-width: 576px) {
    padding: 6px 10px;
    font-size: 12px;
    min-height: 32px;
    gap: 4px;
    
    svg {
      font-size: 12px;
      width: 12px;
      height: 12px;
    }
  }
`;

export const TabsBorderDiv = styled.div`
  border-bottom: 1px solid ${COLORS.border};
  margin-bottom: 20px;
`;

/* Global Styles for Patient Queue */
export const PatientQueueGlobalStyles = createGlobalStyle`
  /* Selected Row Styles */
  .selected-row {
    background-color: rgba(24, 144, 255, 0.1) !important;
  }

  /* Enhanced Animations */
  .filters-animate-in {
    animation: fadeInUp 0.6s ease-out forwards;
    animation-delay: 0.2s;
  }

  .filters-animate-out {
    opacity: 0;
    transform: translateY(20px);
  }

  .stats-animate-in {
    animation: fadeInUp 0.6s ease-out forwards;
    animation-delay: 0.1s;
  }

  .stats-animate-out {
    opacity: 0;
    transform: translateY(20px);
  }

  .header-animate-in {
    animation: fadeInDown 0.6s ease-out forwards;
  }

  .header-animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }

  .table-animate-in {
    animation: fadeInUp 0.6s ease-out forwards;
    animation-delay: 0.3s;
  }

  .table-animate-out {
    opacity: 0;
    transform: translateY(20px);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Row Highlights */
  .in-progress-row {
    background-color: rgba(24, 144, 255, 0.04) !important;
    border-left: 4px solid #1890FF !important;
  }

  .urgent-row {
    background-color: rgba(255, 152, 0, 0.04) !important;
    border-left: 4px solid #FF9800 !important;
  }

  /* Enhanced Status Cards */
  .ant-card:hover {
    transform: translateY(-4px);
    transition: all 0.3s ease;
  }

  /* Table Enhancements */
  .ant-table-tbody > tr:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }

  /* Remove shadow from last row to prevent floating effect */
  .ant-table-tbody > tr:last-child:hover {
    box-shadow: none;
    transform: none;
  }

  /* Button Enhancements */
  .ant-btn:focus,
  .ant-btn:hover {
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }

  /* Input Focus States */
  .ant-input:focus,
  .ant-input-focused,
  .ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  /* Loading States */
  .ant-spin-container {
    transition: opacity 0.3s ease;
  }

  /* Badge Enhancements */
  .ant-badge-count {
    font-weight: 700;
    font-size: 12px;
    min-width: 24px;
    height: 24px;
    line-height: 22px;
  }

  /* Pagination Enhancements */
  .ant-pagination-item:hover {
    transform: translateY(-1px);
    transition: all 0.3s ease;
  }

  .ant-pagination-item-active {
    transform: translateY(-1px);
  }

  .ant-pagination-prev:hover,
  .ant-pagination-next:hover {
    transform: translateY(-1px);
    transition: all 0.3s ease;
  }

  /* Patient Queue Specific Pagination */
  .patient-queue-pagination .ant-pagination {
    background: rgba(248, 248, 248, 0.5);
    padding: 20px;
    border-radius: 8px;
    margin-top: 24px;
  }

  .patient-queue-pagination .ant-pagination-total-text {
    font-weight: 500;
    background: none !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
  }

  /* Menu Enhancements */
  .ant-dropdown-menu {
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    border: 1px solid #e8e8e8;
  }

  .ant-dropdown-menu-item {
    transition: all 0.2s ease;
  }

  .ant-dropdown-menu-item:hover {
    background-color: rgba(24, 144, 255, 0.06);
  }

  /* Mobile Optimizations */
  @media (max-width: 768px) {
    /* Ensure tabs remain perfectly centered on mobile */
    .ant-tabs-nav-wrap {
      display: flex !important;
      justify-content: center !important;
    }
    
    .ant-tabs-nav-list {
      display: flex !important;
      justify-content: center !important;
    }
    
    .ant-tabs-tab-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
      width: 100% !important;
      padding: 0 !important;
    }
    
    .ant-statistic-title {
      font-size: 12px !important;
    }
    
    .ant-statistic-content-value {
      font-size: 24px !important;
    }
    
    .ant-tag {
      font-size: 11px !important;
      padding: 3px 6px !important;
    }

    /* Mobile Pagination Adjustments */
    .patient-queue-pagination .ant-pagination {
      padding: 16px;
    }
    
    .ant-pagination-item,
    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 36px !important;
      height: 36px !important;
      line-height: 32px !important;
    }
    
    .ant-pagination-options-size-changer .ant-select-selector,
    .ant-pagination-options-quick-jumper .ant-input {
      height: 36px !important;
    }
  }

  @media (max-width: 576px) {
    /* Ensure tabs remain perfectly centered on small mobile */
    .ant-tabs-nav-wrap {
      display: flex !important;
      justify-content: center !important;
    }
    
    .ant-tabs-nav-list {
      display: flex !important;
      justify-content: center !important;
    }
    
    .ant-tabs-tab-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
      width: 100% !important;
      padding: 0 !important;
    }
    
    .ant-statistic-title {
      font-size: 11px !important;
    }
    
    .ant-statistic-content-value {
      font-size: 20px !important;
    }
    
    .ant-tag {
      font-size: 10px !important;
      padding: 2px 4px !important;
    }

    /* Small Mobile Pagination Adjustments */
    .patient-queue-pagination .ant-pagination {
      padding: 12px;
    }
    
    .ant-pagination-item,
    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      font-size: 12px !important;
    }
    
    .ant-pagination-total-text {
      font-size: 12px !important;
    }
  }
`;

export const PatientEditContainer = styled.div`
`;

export const HeaderRow = styled(Row)`
  border-bottom: 2px solid ${COLORS.border};
`;

export const PageTitle = styled.h1`
  margin: 0;
  margin-bottom: 8px;
  color: ${COLORS.textDark};
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
`;

export const RefreshButton = styled(ActionButton)`
  width: 36px !important;
  min-width: 36px !important;
  height: 36px !important;
  border-radius: 8px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #333 !important;
  border: 1px solid #333 !important;
  border-color: #333 !important;
  
  &:hover,
  &:hover:not(:disabled) {
    background: #111 !important;
    border-color: #111 !important;
    color: white !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
  }
  
  &:focus,
  &:focus:not(:disabled) {
    background: #333 !important;
    border-color: #333 !important;
    color: white !important;
    box-shadow: 0 0 0 2px rgba(51, 51, 51, 0.2) !important;
  }
  
  &:active,
  &:active:not(:disabled) {
    background: #000 !important;
    border-color: #000 !important;
    color: white !important;
    transform: translateY(0) !important;
  }
  
  .anticon {
    font-size: 14px !important;
    color: white !important;
  }
  
  /* Override any ActionButton styles */
  &.ant-btn {
    background: #333 !important;
    border-color: #333 !important;
    color: white !important;
    
    &:hover {
      background: #111 !important;
      border-color: #111 !important;
      color: white !important;
    }
    
    &:focus {
      background: #333 !important;
      border-color: #333 !important;
      color: white !important;
    }
  }
`;

export const TabBadge = styled.span`
  background-color: ${props => props.color || '#1890ff'};
  border-radius: 10px;
  font-size: 12px;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 11px;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
    padding: 0 5px;
  }
  
  @media (max-width: 576px) {
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
  }
`;

export const FilterFieldContainer = styled.div`
  margin-bottom: 4px;
`;

export const StyledInputWithHeight = styled(StyledInput)`
  height: 48px;
  font-size: 16px;
  
  .ant-input-prefix {
    .anticon {
      color: ${COLORS.textMuted};
      font-size: 16px;
    }
  }
`;

export const StyledSelectWithHeight = styled(StyledSelect)`
  width: 100%;
  height: 48px;
`;

export const TableHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
`;

export const TableTitle = styled.h2`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 20px;
  font-weight: 500;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PatientCountTag = styled.div`
  font-size: 14px;
  font-weight: 600;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const StatusBadge = styled.div`
  background-color: ${props => props.color || COLORS.$primaryBlue};
  border-radius: 10px;
  font-weight: bold;
  font-size: 10px;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
`;

export const PatientInfoContainer = styled.div`
  min-width: 0;
  flex: 1;
`;

export const PatientName = styled.div`
  color: ${COLORS.textDark};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const PatientEmail = styled.div`
  font-size: 13px;
  color: ${COLORS.textMedium};
  display: block;
  margin-top: 2px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const PatientDOB = styled.div`
  font-size: 12px;
  color: ${COLORS.textLight};
  display: block;
  margin-top: 1px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const EventTitle = styled.div`
  font-size: 13px;
  color: ${COLORS.textDark};
  font-weight: 600;
  line-height: 1.3;
`;

export const EventLocation = styled.div`
  font-size: 12px;
  color: ${COLORS.textLight};
  line-height: 1.2;
`;

export const CheckInTime = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.textDark};
  line-height: 1.3;
`;

export const CheckInDate = styled.div`
  font-size: 12px;
  color: ${COLORS.textLight};
  line-height: 1.2;
`;

/* ── Testing Code inline editor ────────────────────────────────── */
export const TestingCodeCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

export const TestingCodeInput = styled(Input)`
  border-radius: 8px !important;
  height: 32px !important;
  font-size: 13px !important;
  background: ${COLORS.backgroundLight} !important;
  border: 1px solid transparent !important;
  box-shadow: none !important;
  transition: all 0.18s ease !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  input {
    font-family: 'Monaco', 'Consolas', 'Liberation Mono', monospace !important;
    letter-spacing: 0.6px;
    background: transparent !important;
    height: 30px !important;
  }

  .ant-input::placeholder {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    letter-spacing: 0;
    color: ${COLORS.textMuted};
  }

  .ant-input-prefix {
    margin-inline-end: 6px;
    color: ${COLORS.textMuted};
  }

  &:hover {
    background: ${COLORS.background} !important;
    border-color: ${COLORS.border} !important;
  }

  &.ant-input-affix-wrapper-focused,
  &:focus-within {
    background: ${COLORS.background} !important;
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.12) !important;
  }

  &.ant-input-affix-wrapper-disabled {
    background: ${COLORS.backgroundLight} !important;
    color: ${COLORS.textMuted} !important;
  }
`;

export const TestingCodeSaveButton = styled(Button)`
  border-radius: 8px !important;
  width: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  line-height: 1 !important;
  box-sizing: border-box !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease !important;

  .anticon {
    font-size: 14px;
    line-height: 1;
  }

  &:not(:disabled) {
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
    border-color: #1890ff !important;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.32) !important;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus {
    /* Keep the exact same footprint & shadow as resting — only brighten */
    width: 32px !important;
    height: 32px !important;
    transform: none !important;
    filter: brightness(1.08);
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.32) !important;
  }

  &:disabled {
    background: ${COLORS.backgroundLight} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textMuted} !important;
    box-shadow: none !important;
  }
`;

export const ResponsiveActionButton = styled(ActionButton)`
  min-width: 80px !important;
  height: 36px !important;
  font-size: 12px !important;
  
  .anticon {
    font-size: 12px;
  }
  
  @media (max-width: 768px) {
    min-width: 36px !important;
    max-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
    padding: 0 !important;
    
    span:not(.anticon) {
      display: none !important;
    }
  }
`;

export const ResponsiveScreeningButton = styled(ActionButton)`
  min-width: 120px !important;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  font-size: 14px !important;
  
  .anticon {
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    min-width: 40px !important;
    max-width: 40px !important;
    width: 40px !important;
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    .ant-btn-icon {
      margin-inline-end: 0 !important;
      
      .anticon {
        font-size: 16px !important;
      }
    }
    
    /* Hide button text, keep icon */
    > span:not(.ant-btn-icon) {
      display: none !important;
    }
  }
`;

export const ResponsiveStatusButton = styled(ActionButton)`
  min-width: 120px !important;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  font-size: 14px !important;
  
  .anticon {
    font-size: 14px;
  }
`;

export const QueueStatsContainer = styled(Row)`
  margin-bottom: 32px;
`;

export const StatisticCard = styled(StatusCard)`
  height: 120px;
`;

export const StatisticTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textMedium};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const StatisticIcon = styled.div`
  color: ${props => props.color || COLORS.$primaryBlue};
  font-size: 20px;
`;

export const PaginationText = styled.span`
  font-size: 14px;
  color: ${COLORS.textMedium};
  font-weight: 500;
`;

export const StyledTableContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  border: 1px solid ${COLORS.border};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* On mobile the card list replaces the boxed table: drop the container
     chrome and let it grow with its content. */
  @media (max-width: 768px) {
    background-color: transparent;
    border: none;
    border-radius: 0;
    overflow: visible;
    box-shadow: none;
    flex: none;
    min-height: 0;
  }
`;

// PatientDetailsModal styled components
export const PatientDetailsModalStyles = createGlobalStyle`
  /* Modal positioning is handled by global CSS in modalResponsive.css */

  .ant-modal-content {
    border-radius: 12px !important;
    overflow: hidden !important;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12) !important;
  }

  .ant-modal-header {
    background: #ffffff !important;
    border-bottom: 1px solid #eef2f7 !important;
    padding: 16px 20px !important;

    .ant-modal-title {
      color: ${COLORS.textDark} !important;
      font-weight: 600 !important;
      font-size: 16px !important;
    }
  }

  .ant-modal-body {
    padding: 14px 16px !important;
    background: #ffffff !important;
  }

  .ant-modal-footer {
    border-top: 1px solid #eef2f7 !important;
    background: #ffffff !important;
    padding: 12px 20px !important;
  }

  /* Mobile: stack footer buttons full-width so they never overflow */
  @media (max-width: 575px) {
    .ant-modal-header {
      padding: 14px 16px !important;
    }

    .ant-modal-footer {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      padding: 12px 16px !important;
    }

    .ant-modal-footer .ant-btn {
      flex: 1 1 100% !important;
      margin: 0 !important;
    }
  }

  /* Mobile: present the patient record as a bottom-sheet Drawer (like filters).
     Footer buttons sit side by side on one row and share the width evenly. */
  @media (max-width: 767px) {
    .patient-details-drawer-footer {
      display: flex;
      flex-wrap: nowrap;
      gap: 8px;
    }

    .patient-details-drawer-footer .ant-btn {
      flex: 1 1 0;
      min-width: 0;
      margin: 0 !important;
      padding: 0 8px !important;
      font-size: 13px !important;
      white-space: nowrap;
    }
  }
`;

export const ScreeningModalTitle = styled(Space)`
  display: flex;
  align-items: center;
`;

export const ScreeningModalIcon = styled.span`
  color: ${COLORS.$mainRed};
  display: flex;
  align-items: center;
`;

export const ModalTitle = styled.div`
  font-size: 21px;
  font-weight: 700;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  letter-spacing: -0.01em;
  padding-right: 24px; /* keep clear of the modal close (x) button */

  @media (max-width: 575px) {
    font-size: 18px;
    gap: 6px;

    .ant-space {
      margin-left: 0 !important;
    }
  }
`;

export const ModalButton = styled(Button)`
  height: 48px;
  font-weight: 500;
  
  &.primary-button {
    background: ${COLORS.gradient.blackPrimary};
    border-color: ${COLORS.$mainBlack};
  }
`;

export const PatientHeaderSection = styled.div`
  padding: 0 0 14px 0;
  margin-bottom: 14px;
  border-bottom: 1px solid #eef2f7;
`;

export const PatientTitle = styled.h3`
  margin: 0 0 6px 0;
  color: ${COLORS.textDark};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

export const PatientIdSection = styled.div`
  text-align: right;
`;

export const PatientIdLabel = styled.span`
  color: ${COLORS.textMedium};
  font-weight: 600;
`;

export const PatientIdCode = styled.code`
  font-size: 14px;
`;

export const StatusBadgeText = styled.span`
  font-weight: 500;
  text-transform: capitalize;
`;

export const ModalContent = styled.div`

`;

export const PatientCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Mobile Filter Drawer for responsive filtering
export const MobileFilterDrawer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .filter-label {
      font-size: 14px;
      font-weight: 600;
      color: ${COLORS.textDark};
      margin-bottom: 4px;
    }
  }
  
  .clear-filters-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid ${COLORS.border};
  }
`;

/* ── Mobile intake cards (replace the table on small screens) ─────── */

// Desktop-only wrapper for the queue table.
export const QueueDesktopTable = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const QueueMobileList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

export const QueueMobileCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 14px 16px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:active { transform: scale(0.99); }

  .qc-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .qc-num {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${COLORS.$primaryBlue};
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  }

  .qc-main {
    flex: 1;
    min-width: 0;
  }

  .qc-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.3;
  }

  .qc-email {
    margin-top: 2px;
    font-size: 13px;
    color: ${COLORS.textLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .qc-location {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${COLORS.textMedium};

    .anticon { color: ${COLORS.textLight}; }
  }

  .qc-time {
    flex-shrink: 0;
    text-align: right;
  }

  .qc-time-value {
    font-size: 13px;
    font-weight: 700;
    color: ${COLORS.textDark};
    white-space: nowrap;
  }

  .qc-time-date {
    font-size: 12px;
    color: ${COLORS.textLight};
    white-space: nowrap;
  }

  .qc-code {
    margin-top: 12px;
  }

  .qc-actions {
    margin-top: 12px;
  }
`;

export const QueueMobilePagination = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 0 4px;

    .pg-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 44px;
      padding: 0 18px;
      border: 1px solid #e6e8ee;
      border-radius: 12px;
      background: #fff;
      color: ${COLORS.textDark};
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        border-color: ${COLORS.$primaryBlue};
        color: ${COLORS.$primaryBlue};
      }

      &:active:not(:disabled) { transform: scale(0.97); }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    .pg-info {
      flex: 1;
      text-align: center;
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.textLight};

      strong { color: ${COLORS.textDark}; }
    }
  }
`;
